import { useDepositModalStore } from '@/stores/deposit-modal-store';
import { useLoginModalStore } from '@/stores/login-modal-store';
import { Suspense, lazy, useEffect, useState } from 'react';
import { AccountDropdown } from './account-dropdown';
import { Button } from './ui/button';
import { motion, useReducedMotion } from 'framer-motion';
import { AudioControls } from './audio-controls';
import { ThemeToggle } from './theme-toggle';
import { UserIcon } from './icons';
import { useAuth } from '@/hooks';
import { ChatButton } from './chat/ChatButton';
import { useChatStore } from '@/stores/chat-store';
import { Menu } from 'lucide-react';
import { cn } from '@/utils';
import { useSidebarStore } from '@/stores/sidebar-store';
import { useWithdrawModalStore } from '@/stores/withdraw-modal-store';
import { useRouterState } from '@tanstack/react-router';
import { useAutoBetTour } from '@/hooks/useAutoBetTour';
import { cancelIdleCallback, scheduleIdleCallback } from '@/utils/idle';

const BalanceDropdownLazy = lazy(() => import('./balance-dropdown').then((mod) => ({ default: mod.BalanceDropdown })));
const CurrencySelectorModalLazy = lazy(() => import('./currency-selector-modal'));
const DepositModalLazy = lazy(() => import('./deposit-modal').then((mod) => ({ default: mod.DepositModal })));
const WithdrawModalLazy = lazy(() => import('./withdraw-modal').then((mod) => ({ default: mod.WithdrawModal })));
const LoginModalLazy = lazy(() => import('./login-modal').then((mod) => ({ default: mod.LoginModal })));
const DailyStreakEntryLazy = lazy(() => import('@/components/daily-streak/DailyStreakEntry').then((mod) => ({ default: mod.DailyStreakEntry })));

const containerVariants: import('framer-motion').Variants = {
	hidden: {},
	show: {
		transition: {
			staggerChildren: 0.08,
			staggerDirection: -1,
		},
	},
};

const itemVariants: import('framer-motion').Variants = {
	hidden: { opacity: 0, y: -32 },
	show: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeInOut' } },
};

const topBarButtonBase =
	'inline-flex h-9 items-center justify-center rounded-full px-2.5 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const topBarIconBase = 'inline-flex h-9 w-9 items-center justify-center rounded-full p-2';
const topBarNeutralSurface =
	'border border-border/60 bg-card/70 text-foreground shadow-sm hover:border-primary/40 hover:bg-card/80 focus-visible:ring-primary/30';

const topBarButtonClass = cn(topBarButtonBase, topBarNeutralSurface);
const topBarIconButtonClass = cn(topBarIconBase, topBarNeutralSurface);
const balanceFallbackClass = (highlight: boolean) =>
	cn('h-9 w-[172px] rounded-full border border-border/60 bg-card/60 shadow-sm animate-pulse', highlight && 'pulse pulse-yellow');

export function TopMenu() {
	const { isLogged, disconnect, authType, address } = useAuth();
	const isChatOpen = useChatStore((s) => s.isOpen);
	const toggleSidebar = useSidebarStore((s) => s.toggle);
	const isSidebarOpen = useSidebarStore((s) => s.isMobileOpen);

	const { openLoginModal } = useLoginModalStore();
	const { openDepositModal } = useDepositModalStore();
	const { openWithdrawModal } = useWithdrawModalStore();
	const isLoginModalOpen = useLoginModalStore((s) => s.isOpen);
	const isDepositModalOpen = useDepositModalStore((s) => s.isOpen);
	const isWithdrawModalOpen = useWithdrawModalStore((s) => s.isOpen);
	const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false);
	const [shouldLoadUserUI, setShouldLoadUserUI] = useState(false);

	const { stage: autoBetStage, isActive: isAutoBetTourActive, stop: stopAutoBetTour } = useAutoBetTour();

	useEffect(() => {
		if (!isLogged) {
			setShouldLoadUserUI(false);
			return;
		}
		const handle = scheduleIdleCallback(() => setShouldLoadUserUI(true), 1400);
		return () => cancelIdleCallback(handle);
	}, [isLogged]);

	const handleDisconnect = async () => {
		try {
			const { closeLoginModal } = useLoginModalStore.getState();
			disconnect();
			closeLoginModal();
		} catch (error) {
			console.error('Error during disconnect:', error);
		}
	};

	const prefersReducedMotion = useReducedMotion();
	const [isDesktop, setIsDesktop] = useState<boolean>(() =>
		typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : false,
	);
	const animationsEnabled = isDesktop && !prefersReducedMotion;

	useEffect(() => {
		if (typeof window === 'undefined') return;
		const mediaQuery = window.matchMedia('(min-width: 768px)');
		const handleChange = (event: MediaQueryListEvent) => {
			setIsDesktop(event.matches);
		};
		setIsDesktop(mediaQuery.matches);
		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, []);

	const handleCurrencyModalDeposit = () => {
		setIsCurrencyModalOpen(false);
		openDepositModal(false);
	};

	const handleCurrencyModalWithdraw = () => {
		if (authType !== 'zklogin') return;
		setIsCurrencyModalOpen(false);
		openWithdrawModal();
	};
	const { location } = useRouterState();
	const isSuiRoyaleRoute = location.pathname.startsWith('/suiroyale');
	const highlightDesktopDeposit = Boolean(isAutoBetTourActive && autoBetStage === 'deposit-entry' && isLogged && isDesktop);
	const highlightBalanceDropdown = Boolean(isAutoBetTourActive && autoBetStage === 'deposit-entry' && !isDesktop);

	useEffect(() => {
		if (!isAutoBetTourActive) return;
		if (!isLogged && autoBetStage !== 'login') {
			stopAutoBetTour();
		}
	}, [autoBetStage, isAutoBetTourActive, isLogged, stopAutoBetTour]);

	return (
		<>
			<motion.div
				key={animationsEnabled ? 'desktop' : 'mobile'}
				className={`fixed z-30 right-0 flex w-full max-md:p-4 md:top-4 md:px-4 ${isChatOpen ? 'md:pr-84' : 'md:pr-4'}`}
				variants={containerVariants}
				initial={animationsEnabled ? 'hidden' : 'show'}
				animate="show"
			>
				<motion.button
					variants={animationsEnabled ? itemVariants : undefined}
					initial={animationsEnabled ? 'hidden' : false}
					animate={animationsEnabled ? 'show' : undefined}
					style={!animationsEnabled ? { opacity: 1, transform: 'none' } : undefined}
					aria-controls="default-sidebar"
					aria-expanded={isSidebarOpen}
					type="button"
					className={cn(topBarIconButtonClass, 'md:hidden cursor-pointer')}
					onClick={toggleSidebar}
				>
					<span className="sr-only">Open sidebar</span>
					<Menu className="h-5 w-5" aria-hidden="true" />
				</motion.button>
				<div className="flex w-full justify-end gap-2">
					{!isLogged ? (
						<motion.div
							variants={animationsEnabled ? itemVariants : undefined}
							initial={animationsEnabled ? 'hidden' : false}
							animate={animationsEnabled ? 'show' : undefined}
							style={!animationsEnabled ? { opacity: 1, transform: 'none' } : undefined}
						>
							<div className="flex items-center gap-2">
								{!isSuiRoyaleRoute ? <ThemeToggle size="compact" /> : null}
								<AudioControls className="items-center" size="compact" />
							</div>
						</motion.div>
					) : (
						<>
							<motion.div
								className="relative flex items-center gap-2"
								variants={animationsEnabled ? itemVariants : undefined}
								initial={animationsEnabled ? 'hidden' : false}
								animate={animationsEnabled ? 'show' : undefined}
								style={!animationsEnabled ? { opacity: 1, transform: 'none' } : undefined}
							>
								{shouldLoadUserUI ? (
									<Suspense fallback={<div className={balanceFallbackClass(highlightBalanceDropdown)} />}>
										<BalanceDropdownLazy address={address} authType={authType} highlightAsAutoBetTarget={highlightBalanceDropdown} />
									</Suspense>
								) : (
									<div className={balanceFallbackClass(highlightBalanceDropdown)} />
								)}
							</motion.div>
							<motion.div
								className="hidden md:inline-flex"
								variants={animationsEnabled ? itemVariants : undefined}
								initial={animationsEnabled ? 'hidden' : false}
								animate={animationsEnabled ? 'show' : undefined}
								style={!animationsEnabled ? { opacity: 1, transform: 'none' } : undefined}
							>
								<Button
									className={cn('h-9 items-center gap-2 px-5 font-semibold', highlightDesktopDeposit && 'pulse pulse-yellow')}
									onClick={() => openDepositModal(false)}
									variant="primary"
									size="sm"
								>
									Deposit
								</Button>
							</motion.div>
						</>
					)}
					<motion.div
						variants={animationsEnabled ? itemVariants : undefined}
						initial={animationsEnabled ? 'hidden' : false}
						animate={animationsEnabled ? 'show' : undefined}
						style={!animationsEnabled ? { opacity: 1, transform: 'none' } : undefined}
					>
						{isLogged ? (
							<div className="flex gap-2">
								{/* Daily Streak Flame next to username */}
								{shouldLoadUserUI ? (
									<Suspense fallback={null}>
										<DailyStreakEntryLazy />
									</Suspense>
								) : null}
								<AccountDropdown logoutBtnOnClick={handleDisconnect} triggerClassName={topBarButtonClass} hideThemeToggle={isSuiRoyaleRoute} />
							</div>
						) : (
							<>
								<Button id="loginButton" className={cn('gap-2', topBarButtonClass)} variant="ghost" onClick={() => openLoginModal()}>
									<UserIcon className="h-4 w-4" />
									<span>Sign In</span>
								</Button>
							</>
						)}
					</motion.div>
					{/* Global Chat button */}
					<motion.div
						variants={animationsEnabled ? itemVariants : undefined}
						initial={animationsEnabled ? 'hidden' : false}
						animate={animationsEnabled ? 'show' : undefined}
						style={!animationsEnabled ? { opacity: 1, transform: 'none' } : undefined}
						className="hidden md:flex"
					>
						<ChatButton className={topBarIconButtonClass} />
					</motion.div>
				</div>
			</motion.div>

			{isCurrencyModalOpen ? (
				<Suspense fallback={null}>
					<CurrencySelectorModalLazy
						isOpen={isCurrencyModalOpen}
						onClose={() => setIsCurrencyModalOpen(false)}
						onDeposit={handleCurrencyModalDeposit}
						onWithdraw={handleCurrencyModalWithdraw}
						canWithdraw={authType === 'zklogin'}
						context="balance"
						title="Balance"
					/>
				</Suspense>
			) : null}
			{isDepositModalOpen ? (
				<Suspense fallback={null}>
					<DepositModalLazy />
				</Suspense>
			) : null}
			{isWithdrawModalOpen ? (
				<Suspense fallback={null}>
					<WithdrawModalLazy />
				</Suspense>
			) : null}
			{isLoginModalOpen ? (
				<Suspense fallback={null}>
					<LoginModalLazy />
				</Suspense>
			) : null}
		</>
	);
}
export default TopMenu;
